import { Dispatch } from "redux"
import { toNumber, merge, clone, concat, reject, isEmpty, get } from "lodash"
import uuid from "uuid/v4"

import { API_MESSAGE } from "./Push"
import APIs from "redux/apis"
import { APIResponse } from "redux/apis/types"
import { pageLoadRegist } from "redux/ducks/PageLoad"
import store from "../store"
import { setToastData } from "./Toast"
import { UserStorage } from "utils/userStorage"

export type SymbolSheetType = {
  SheetNo?: string
  SheetName?: string
  SymbolList?: Array<APIResponse.Res>
} & APIResponse.Res

export type SymbolSheetListType = {
  MaxSymbolCount?: number
  SheetList?: Array<{
    SheetNo: string
    SheetName: string
    RegisteredCount: number
  }>
} & APIResponse.Res

export type RegisterSymbolStateType = {
  symbolSheet: SymbolSheetType
  symbolSheetList: SymbolSheetListType
  loadingId: string
}

/************************************
 * ACTION TYPE
 ************************************/
export const REDUX_REGISTER_SYMBOL_LOAD = "REDUX_REGISTER_SYMBOL_LOAD"
export const REDUX_REGISTER_SYMBOL_CHANGE_SHEET =
  "REDUX_REGISTER_SYMBOL_CHANGE_SHEET"
export const REDUX_REGISTER_SYMBOL_CHANGE_SHEET_NAME =
  "REDUX_REGISTER_SYMBOL_CHANGE_SHEET_NAME"
export const REDUX_REGISTER_SYMBOL_EDIT_SHEET =
  "REDUX_REGISTER_SYMBOL_EDIT_SHEET"
export const REDUX_REGISTER_SYMBOL_EDIT_LIST = "REDUX_REGISTER_SYMBOL_EDIT_LIST"

type RegisterSymbolActionType = {
  type:
    | typeof REDUX_REGISTER_SYMBOL_LOAD
    | typeof REDUX_REGISTER_SYMBOL_CHANGE_SHEET
    | typeof REDUX_REGISTER_SYMBOL_CHANGE_SHEET_NAME
    | typeof REDUX_REGISTER_SYMBOL_EDIT_SHEET
    | typeof REDUX_REGISTER_SYMBOL_EDIT_LIST
  symbolSheet?: SymbolSheetType
  symbolSheetList?: SymbolSheetListType
}

/************************************
 * ACTIONS
 ************************************/
//ローディング用に
const loadingId = uuid()

// anyあとで直す
export const initRegisterLoad: () => any = () => {
  return async (dispatch: Dispatch) => {
    await dispatch(pageLoadRegist(loadingId, true))
    const prevSheet = UserStorage.registerSheet.get()
    const symbolSheetList: SymbolSheetListType = await APIs.Tool.SymbolSheetList()
    const symbolSheet: SymbolSheetType = await APIs.Tool.SymbolSheet({
      SheetNo: isEmpty(prevSheet) ? 0 : get(prevSheet, "sheet"),
    })
    await dispatch(pageLoadRegist(loadingId, false))
    dispatch({
      type: API_MESSAGE,
      value: symbolSheet.SymbolList,
    })
    dispatch({
      type: REDUX_REGISTER_SYMBOL_LOAD,
      symbolSheetList: symbolSheetList,
      symbolSheet: symbolSheet,
    })
  }
}

export const changeRegisterSheet: (sheetNo: string) => any = sheetNo => {
  return async (dispatch: Dispatch) => {
    await dispatch(pageLoadRegist(loadingId, true))
    UserStorage.registerSheet.set({ sheet: toNumber(sheetNo) })
    const symbolSheet: SymbolSheetType = await APIs.Tool.SymbolSheet({
      SheetNo: toNumber(sheetNo),
    })
    await dispatch(pageLoadRegist(loadingId, false))
    dispatch({
      type: API_MESSAGE,
      value: symbolSheet.SymbolList,
    })
    dispatch({
      type: REDUX_REGISTER_SYMBOL_CHANGE_SHEET,
      symbolSheet: symbolSheet,
    })
  }
}

export const changeRegisterSheetName: (
  sheetName: string,
  currentSheet: SymbolSheetType
) => any = (sheetName, currentSheet) => {
  return async (dispatch: Dispatch) => {
    await dispatch(pageLoadRegist(loadingId, true))
    const changeSheet = merge(currentSheet, { SheetName: sheetName })
    const editSymbolSheet: APIResponse.POSTRes = await APIs.Tool.EditSymbolSheet(
      changeSheet
    )
    await dispatch(pageLoadRegist(loadingId, false))
    if (!editSymbolSheet.Result) {
      const symbolSheetList: SymbolSheetListType = await APIs.Tool.SymbolSheetList()
      dispatch({
        type: REDUX_REGISTER_SYMBOL_CHANGE_SHEET_NAME,
        symbolSheet: changeSheet,
        symbolSheetList: symbolSheetList,
      })
    } else {
      //todo　ここにエラー時処理を後に追加
      return
    }
  }
}

export const addRegisterSymbolSheet: (
  data: APIResponse.Res,
  currentList: SymbolSheetType
) => any = (data, currentList) => {
  if (!data) return
  return async (dispatch: Dispatch) => {
    await dispatch(pageLoadRegist(loadingId, true))
    const symbolList = clone(currentList.SymbolList)
    const changedList = merge(currentList, {
      SymbolList: concat(symbolList, data),
    })
    const editSymbolSheet: APIResponse.POSTRes = await APIs.Tool.EditSymbolSheet(
      changedList
    )
    await dispatch(pageLoadRegist(loadingId, false))
    if (!editSymbolSheet.Result) {
      dispatch({
        type: API_MESSAGE,
        value: data,
      })
      dispatch({
        type: REDUX_REGISTER_SYMBOL_EDIT_SHEET,
        symbolSheet: changedList,
      })
      store.dispatch(
        setToastData({
          bodyMessage: "登録しました",
        })
      )
    } else {
      store.dispatch(
        setToastData({
          bodyMessage: "登録件数が上限を超えているため登録できません。",
        })
      )
    }
  }
}

export const removeRegisterSymbolSheet: (
  targetNum: number,
  currentList: SymbolSheetType
) => any = (targetNum, currentList) => {
  return async (dispatch: Dispatch) => {
    await dispatch(pageLoadRegist(loadingId, true))
    const symbolList = clone(currentList.SymbolList)
    currentList["SymbolList"] = reject(symbolList, (row, i) => i === targetNum)
    const editSymbolSheet: APIResponse.POSTRes = await APIs.Tool.EditSymbolSheet(
      currentList
    )
    await dispatch(pageLoadRegist(loadingId, false))
    if (!editSymbolSheet.Result) {
      dispatch({
        type: REDUX_REGISTER_SYMBOL_EDIT_SHEET,
        symbolSheet: currentList,
      })
    }
  }
}

export const editRegisterSymbolSheet: (list: SymbolSheetType) => any = list => {
  return async (dispatch: Dispatch) => {
    await dispatch(pageLoadRegist(loadingId, true))
    const editSymbolSheet: APIResponse.POSTRes = await APIs.Tool.EditSymbolSheet(
      list
    )
    const symbolSheet: SymbolSheetType = await APIs.Tool.SymbolSheet({
      SheetNo: list.SheetNo,
    })
    await dispatch(pageLoadRegist(loadingId, false))
    if (!editSymbolSheet.Result) {
      dispatch({
        type: API_MESSAGE,
        value: symbolSheet.SymbolList,
      })
      dispatch({
        type: REDUX_REGISTER_SYMBOL_EDIT_LIST,
        symbolSheet: symbolSheet,
      })
    }
  }
}

/************************************
 * Reducer
 ************************************/
const initialState: RegisterSymbolStateType = {
  symbolSheetList: {
    SheetList: [],
  },
  symbolSheet: {
    SymbolList: [],
  },
  loadingId: loadingId,
}
export default (
  state: RegisterSymbolStateType = initialState,
  action: RegisterSymbolActionType
) => {
  switch (action.type) {
    case REDUX_REGISTER_SYMBOL_LOAD:
      return {
        ...state,
        symbolSheet: action.symbolSheet,
        symbolSheetList: action.symbolSheetList,
      }
    case REDUX_REGISTER_SYMBOL_CHANGE_SHEET:
      return {
        ...state,
        symbolSheet: action.symbolSheet,
      }
    case REDUX_REGISTER_SYMBOL_CHANGE_SHEET_NAME:
      return {
        ...state,
        symbolSheet: action.symbolSheet,
        symbolSheetList: action.symbolSheetList,
      }
    case REDUX_REGISTER_SYMBOL_EDIT_SHEET:
      return {
        ...state,
        symbolSheet: action.symbolSheet,
      }
    case REDUX_REGISTER_SYMBOL_EDIT_LIST:
      return {
        ...state,
        symbolSheet: action.symbolSheet,
      }
    default:
      return state
  }
}
